import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import * as URLS from '../constants/urls';
import { parseFullDateFromDb, formatDateDisplay } from '../utils/date';
import * as api from '../utils/api';
import { TC_COLORS, HOME_CHART_COLOR } from '../constants/colors';

export const CHART_COLORS = {
  BLUE: '#3366cc',
  GREEN: '#008e00',
  RED: '#b30303',
  YELLOW: '#e1c103',
  BLACK: '#000',
  GREEN2: 'rgba(0, 142, 0, 0.5)',
  PURPLE: '#FF25FF',
};

const LIMIT = 18;

const transferNewsData = (news) => {
  const data = [];
  const dates = [];
  for (let i = 0; i < news.length; i++) {
    const date = formatDateDisplay(Number(news[i].publishedAt));
    if (!dates.includes(date)) {
      const items = news.filter(
        (n) => formatDateDisplay(Number(n.publishedAt)) === date
      );
      data.push({
        date: Number(news[i].publishedAt),
        news: items,
      });
      dates.push(date);
    }
  }
  return data;
};

export const financialInvestmentLegends = [
  {
    label: 'Doanh thu thuần',
    value: 'doanhthuthuan',
    color: TC_COLORS.YELLOW,
  },
  {
    label: 'Lợi nhuận ròng',
    value: 'lnsauthue',
    color: TC_COLORS.GREEN,
  },
  {
    label: 'Tổng nợ vay',
    value: 'tongnophaitra',
    color: TC_COLORS.RED,
  },
];

export const financialLegends = [
  {
    label: 'Tổng tài sản',
    value: 'tongtaisan',
    color: HOME_CHART_COLOR.pink1,
  },
  {
    label: 'Doanh thu',
    value: 'doanhthuthuan',
    color: CHART_COLORS.YELLOW,
  },
  {
    label: 'Tổng nợ phải trả',
    value: 'tongnophaitra',
    color: CHART_COLORS.RED,
  },
  {
    label: 'Lợi nhuận trước thuế',
    value: 'lntruocthue',
    color: CHART_COLORS.GREEN,
  },
  {
    label: 'Lợi nhuận sau thuế',
    value: 'lnsauthue',
    color: CHART_COLORS.GREEN2,
  },
];

export const financialPeriods = [
  {
    label: 'Theo quý',
    value: 'quarter',
  },
  {
    label: 'Theo năm',
    value: 'year',
  },
];

const useInvestmentModal = () => {
  const [isNewsLoading, setNewsLoading] = useState(true);
  const [selectedModal, setSelectedModal] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [totalPage, setTotalPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [news, setNews] = useState([]);

  const [isChartLoading, setChartLoading] = useState(true);
  const [selectedChart, setSelectedChart] = useState('');
  const [chartSelectedStock, setChartSelectedStock] = useState(null);
  const [chartVolumeData, setChartVolumeData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [isFinancialChartLoading, setFinancialChartLoading] = useState(true);
  const [financialChartType, setFinancialChartType] = useState(
    financialPeriods[0].value
  );
  const [financialField, setFinancialField] = useState([
    financialLegends[0].value,
  ]);
  const [financialData, setFinancialData] = useState([]);

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const { data } = await api.get(
        `${URLS.GET_NEWS_URL}?stock=${selectedStock?.code}&offset=${
          LIMIT * (currentPage - 1)
        }&limit=${LIMIT}`
      );
      setNews(transferNewsData(data.news));
      if (currentPage === 1) {
        setTotalPage(Math.ceil(data.totalItems / LIMIT));
      }
    } catch (error) {
      console.error(error);
    }
    setNewsLoading(false);
  };

  const fetchTradingVolumeChartByCode = async () => {
    setChartLoading(true);
    try {
      const { data } = await api.get(
        `${URLS.GET_TRADING_VOLUME_CHART_URL}?endDate=${dayjs().format(
          'YYYYMMDD'
        )}&startDate=${dayjs(
          parseFullDateFromDb(
            selectedPeriod || chartSelectedStock?.purchaseDate
          )
        ).format('YYYYMMDD')}&code=${chartSelectedStock?.code}`
      );
      setChartVolumeData(data);
    } catch (error) {
      console.error(error);
    }
    setChartLoading(false);
  };

  const fetchFinancialChartByCode = async () => {
    setFinancialChartLoading(true);
    try {
      const { data } = await api.get(
        `${
          URLS.GET_FINANCIAL_CHART_URL
        }?type=${financialChartType}&field=${financialField.join(',')}&stock=${
          chartSelectedStock?.code
        }&sectorId=${chartSelectedStock?.sectorId}`
      );
      setFinancialData(data);
    } catch (error) {
      console.error(error);
    }
    setFinancialChartLoading(false);
  };

  const handleOpenNewsModal = (stock) => {
    setCurrentPage(1);
    setSelectedStock(stock);
    setSelectedModal('news');
  };

  const handleCloseNewsModal = () => {
    setSelectedModal('');
    setCurrentPage(1);
    setSelectedStock('');
  };

  useEffect(() => {
    if (selectedModal === 'news') {
      fetchNews();
    }
  }, [currentPage, selectedStock, selectedModal]);

  useEffect(() => {
    if (selectedModal === 'chart') {
      if (selectedChart === 'volume') {
        fetchTradingVolumeChartByCode();
      } else {
        fetchFinancialChartByCode();
      }
    }
  }, [
    selectedModal,
    chartSelectedStock,
    selectedChart,
    financialChartType,
    financialField,
  ]);

  useEffect(() => {
    if (!selectedPeriod && chartSelectedStock?.purchaseDate) {
      setSelectedPeriod(
        dayjs(parseFullDateFromDb(chartSelectedStock?.purchaseDate)).format(
          'YYYYMMDD'
        )
      );
    }
  }, [chartSelectedStock?.purchaseDate]);

  useEffect(() => {
    if (selectedPeriod) {
      fetchTradingVolumeChartByCode();
    }
  }, [selectedPeriod]);

  const handleOpenChartModal = ({
    type,
    code,
    name,
    label,
    purchaseDate,
    sectorId,
  }) => {
    setChartSelectedStock({
      code,
      name,
      label,
      purchaseDate,
      sectorId,
    });
    setFinancialField([type]);
    setSelectedChart(type);
    setSelectedModal('chart');
    setSelectedStock({ code, name, label });
  };

  const handleCloseChartModal = () => {
    setSelectedChart('');
    setSelectedModal('');
  };

  const getPeriods = () => {
    if (selectedChart === 'volume') {
      return [
        {
          value: dayjs(
            parseFullDateFromDb(chartSelectedStock?.purchaseDate)
          ).format('YYYYMMDD'),
          label: 'Từ lúc mua',
          alias: 'từ lúc mua',
        },
        {
          value: dayjs().subtract(12, 'month').format('YYYYMMDD'),
          label: '12T',
          alias: '12 tháng',
        },
        {
          value: dayjs().subtract(24, 'month').format('YYYYMMDD'),
          label: '24T',
          alias: '24 tháng',
        },
        {
          value: dayjs().subtract(36, 'month').format('YYYYMMDD'),
          label: '36T',
          alias: '36 tháng',
        },
        {
          value: 0,
          label: 'Tất cả',
        },
      ];
    }

    return null;
  };

  const handlePeriodClick = (type, value) => {
    setSelectedPeriod(value);
  };

  const handleLegendChange = (value) => {
    const newFinancialField = financialField;
    if (newFinancialField.includes(value)) {
      newFinancialField.splice(newFinancialField.indexOf(value), 1);
    } else {
      newFinancialField.push(value);
    }
    setFinancialField([...newFinancialField]);
  };

  return {
    isNewsLoading,
    selectedModal,
    totalPage,
    news,
    selectedStock,
    currentPage,
    handleOpenNewsModal,
    handleCloseNewsModal,
    setCurrentPage,
    isChartLoading,
    selectedChart,
    handleOpenChartModal,
    handleCloseChartModal,
    chartVolumeData,
    getPeriods,
    selectedPeriod,
    handlePeriodClick,
    isFinancialChartLoading,
    financialField,
    financialChartType,
    setFinancialChartType,
    handleLegendChange,
    financialData,
  };
};

export default useInvestmentModal;
